import { HttpApiService } from "@/shared/services/httpApi.service";

import { WALLET_DEBITS_URL } from "../constants/walletDebits.constants";
import type {
  CreateWalletDebitPayload,
  ListWalletDebitsParams,
  WalletDebit,
  WalletDebitCreated,
  WalletDebitEvent,
  WalletDebitSendPreview,
  WalletDebitsPage,
} from "../types/walletDebits.types";

/**
 * `/internal/payments/wallet-debits` — the only place that knows the wire. Who may use it is answered by the
 * top-ups access endpoint: debits share its allowlist.
 */
export const WalletDebitsService = {
  async list(params: ListWalletDebitsParams): Promise<WalletDebitsPage> {
    const { data } = await HttpApiService.get<WalletDebitsPage>(WALLET_DEBITS_URL, { params });
    return data;
  },

  async get(id: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.get<WalletDebit>(`${WALLET_DEBITS_URL}/${id}`);
    return data;
  },

  /** The debit's timeline, oldest first. */
  async events(id: string): Promise<WalletDebitEvent[]> {
    const { data } = await HttpApiService.get<WalletDebitEvent[]>(`${WALLET_DEBITS_URL}/${id}/events`);
    return data;
  },

  /** What to send by hand for a debit of `amount`, per payout corridor of the wallet currency. */
  async sendPreview(
    organizationId: string,
    walletId: string,
    amount: string,
  ): Promise<WalletDebitSendPreview> {
    const { data } = await HttpApiService.get<WalletDebitSendPreview>(`${WALLET_DEBITS_URL}/send-preview`, {
      params: { organization_id: organizationId, wallet_id: walletId, amount },
    });
    return data;
  },

  /** What became of a create whose answer was lost: the debit, or 404 not found / 409 in progress. */
  async findByKey(organizationId: string, idempotencyKey: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.get<WalletDebit>(
      `${WALLET_DEBITS_URL}/by-key/${encodeURIComponent(idempotencyKey)}`,
      { params: { organization_id: organizationId } },
    );
    return data;
  },

  /** Reserves the debit: the amount is held until its send is recorded or it is cancelled. */
  async create(payload: CreateWalletDebitPayload, twoFactorCode: string): Promise<WalletDebitCreated> {
    const { data } = await HttpApiService.post<WalletDebitCreated>(WALLET_DEBITS_URL, payload, {
      headers: { "x-two-factor-code": twoFactorCode },
    });
    return data;
  },

  /** Records the manual send of a reserved debit. No 2FA: the money was held when it was reserved. */
  async markSent(id: string, externalReference: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.post<WalletDebit>(`${WALLET_DEBITS_URL}/${id}/mark-sent`, {
      external_reference: externalReference,
    });
    return data;
  },

  /** Gives a reserved debit's amount back. Behind the second factor, like reserving it. */
  async cancel(id: string, reason: string, twoFactorCode: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.post<WalletDebit>(
      `${WALLET_DEBITS_URL}/${id}/cancel`,
      { reason },
      { headers: { "x-two-factor-code": twoFactorCode } },
    );
    return data;
  },

  /** Corrects a sent debit's reference; the timeline keeps the old one. */
  async updateReference(id: string, externalReference: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.patch<WalletDebit>(`${WALLET_DEBITS_URL}/${id}/reference`, {
      external_reference: externalReference,
    });
    return data;
  },

  /** The proof of the send, as `multipart/form-data` under `attachments`. No 2FA: it moves no money. */
  async uploadAttachments(id: string, files: File[]): Promise<WalletDebit> {
    const body = new FormData();
    for (const file of files) body.append("attachments", file, file.name);
    const { data } = await HttpApiService.post<WalletDebit>(`${WALLET_DEBITS_URL}/${id}/attachments`, body);
    return data;
  },

  /** Takes a file off the debit; it stays stored and the timeline names it. */
  async removeAttachment(id: string, attachmentId: string): Promise<WalletDebit> {
    const { data } = await HttpApiService.delete<WalletDebit>(
      `${WALLET_DEBITS_URL}/${id}/attachments/${attachmentId}`,
    );
    return data;
  },
};
